import React, { useEffect, useState } from 'react';
import { TrendingUp, Loader2, Plus, Lightbulb } from 'lucide-react';
import { Card, Btn, Empty, LabeledInput } from './shared';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const OverloadCard = () => {
  const [exercises, setExercises] = useState([]);
  const [exercise, setExercise] = useState('');
  const [history, setHistory] = useState([]);
  const [next, setNext] = useState(null);
  const [form, setForm] = useState({ weight_kg: 40, sets: 3, reps: 8, rir: 2, notes: '' });
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  const loadExercises = async () => {
    try {
      const xs = await api.liftExercises();
      setExercises(xs || []);
      if (xs && xs.length && !exercise) setExercise(xs[0]);
    } catch {}
  };

  useEffect(() => { loadExercises(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (!exercise) { setHistory([]); setNext(null); return; }
    Promise.all([api.listLifts(exercise), api.nextLift(exercise)])
      .then(([h, n]) => { setHistory(h || []); setNext(n); })
      .catch(() => {});
  }, [exercise]);

  const setNum = (k) => (e) => setForm({ ...form, [k]: Number(e.target.value) });

  const submit = async (e) => {
    e.preventDefault();
    const ex = exercise.trim();
    if (!ex) { toast({ title: 'Pick or type an exercise', variant: 'destructive' }); return; }
    setBusy(true);
    try {
      await api.addLift({ exercise: ex, ...form });
      toast({ title: 'Lift logged', description: `${ex}: ${form.weight_kg}kg × ${form.reps} (RIR ${form.rir})` });
      await loadExercises();
      const [h, n] = await Promise.all([api.listLifts(ex), api.nextLift(ex)]);
      setHistory(h || []); setNext(n);
    } catch (err) {
      toast({ title: 'Could not log', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <Card title="Progressive overload" icon={TrendingUp}>
      <p className="text-[#aab1bf] text-sm -mt-2 mb-4">Log each lift with weight, reps and reps-in-reserve. We compute the next prescription automatically.</p>

      <div className="grid lg:grid-cols-[1fr_1fr] gap-4">
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-[#cbd5e1] font-medium text-sm">Exercise</label>
            <div className="flex gap-2 mt-1.5">
              <input
                list="exercises-list" value={exercise} onChange={(e) => setExercise(e.target.value)}
                placeholder="e.g. Goblet squat"
                className="flex-1 bg-[#0b0c10] border border-[#2a2f3a] focus:border-[#ff7a1a] focus:ring-2 focus:ring-[#ff7a1a]/20 rounded-lg px-3 py-2.5 text-white outline-none text-sm"
              />
              <datalist id="exercises-list">
                {exercises.map((x) => <option key={x} value={x} />)}
              </datalist>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <LabeledInput label="Weight (kg)" type="number" step="0.5" value={form.weight_kg} onChange={setNum('weight_kg')} />
            <LabeledInput label="Sets" type="number" min="1" max="20" value={form.sets} onChange={setNum('sets')} />
            <LabeledInput label="Reps" type="number" min="1" max="50" value={form.reps} onChange={setNum('reps')} />
            <LabeledInput label="RIR" hint="Reps in reserve" type="number" min="0" max="10" value={form.rir} onChange={setNum('rir')} />
          </div>
          <Btn type="submit" disabled={busy} className="w-full">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Log lift
          </Btn>
        </form>

        <div className="space-y-3">
          {next ? (
            <div className="rounded-xl border border-[#3a2310] bg-gradient-to-br from-[#1a0f06] to-[#0b0c10] p-4">
              <p className="flex items-center gap-1.5 text-[#ff7a1a] text-[10px] uppercase font-bold tracking-wider"><Lightbulb className="w-3.5 h-3.5" /> Next session for {exercise || 'this lift'}</p>
              <p className="text-white font-extrabold text-lg mt-2 leading-tight">{next.recommendation}</p>
              <p className="text-[#cbb39a] text-xs mt-2">{next.rationale}</p>
            </div>
          ) : <Empty>Log your first set to get a next-session prescription.</Empty>}

          {history.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider font-bold text-[#7c8597]">Recent</p>
              <ul className="mt-2 space-y-1.5 max-h-48 overflow-y-auto pr-1">
                {history.slice(0, 8).map((h) => (
                  <li key={h.id} className="flex items-center justify-between text-sm border border-[#1f2937] bg-[#0b0c10] rounded-lg px-3 py-2">
                    <span className="text-white font-semibold">{h.weight_kg}kg × {h.reps} × {h.sets}</span>
                    <span className="text-[#7c8597] text-xs">RIR {h.rir} · {new Date(h.created_at).toLocaleDateString()}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default OverloadCard;
